import { v4 as uuidv4 } from 'uuid';
import { DEFAULT_LIKERT_LABELS } from '../constants/review-constants';
import { Question, QuestionFormatType, QuestionOption, Section } from '../types/form-engine.types';

export const createQuestion = (formatType: QuestionFormatType = 'short-text'): Question => {
  const isChoice = formatType === 'multiple-choice' || formatType === 'checkboxes';
  const isRating = formatType === 'rating' || formatType === 'nps';
  const isLikert = formatType === 'likert-scale';

  return {
    ItemId: uuidv4(),
    FormatType: formatType,
    Question: '',
    QuestionDescription: '',
    Options: isChoice
      ? [
          { Label: 'Option 1', Value: 'Option 1', Action: 'continue' },
          { Label: 'Option 2', Value: 'Option 2', Action: 'continue' },
        ]
      : [],
    MinRating: 1,
    MaxRating: 5,
    MinRatingLabel: isRating ? 'Very Poor' : undefined,
    MaxRatingLabel: isRating ? 'Excellent' : undefined,
    LikertLabels: isLikert ? [...DEFAULT_LIKERT_LABELS] : [],
    LikertStatements: isLikert
      ? [
          { Statement: 'Statement 1', Value: [] },
          { Statement: 'Statement 2', Value: [] },
        ]
      : [],
    Position: 0,
    IsRequired: true,
    HasComment: false,
    Answer: '',
    Rating: 0,
    McOptionsValue: '',
    CbOptionsValues: [],
    Comments: '',
  };
};

export const createSection = (position: number): Section => ({
  SectionId: uuidv4(),
  Title: '',
  Description: '',
  Position: position,
  Questions: [],
});

/** Switching format resets every answer/config field the previous format owned (legacy changeType). */
export const changeQuestionFormat = (question: Question, formatType: QuestionFormatType): Question => {
  const fresh = createQuestion(formatType);
  return {
    ...question,
    FormatType: formatType,
    Options: fresh.Options,
    MinRating: formatType === 'likert-scale' ? 1 : question.MinRating || 1,
    MaxRating: formatType === 'likert-scale' ? 5 : question.MaxRating || 5,
    MinRatingLabel: fresh.MinRatingLabel ?? question.MinRatingLabel,
    MaxRatingLabel: fresh.MaxRatingLabel ?? question.MaxRatingLabel,
    LikertLabels: fresh.LikertLabels,
    LikertStatements: fresh.LikertStatements,
    IsPromotionTierQuestion: formatType === 'multiple-choice' ? question.IsPromotionTierQuestion : false,
    IsPromotionRecommendationQuestion: formatType === 'multiple-choice' ? question.IsPromotionRecommendationQuestion : false,
    Answer: '',
    Rating: 0,
    McOptionsValue: '',
    CbOptionsValues: [],
  };
};

export const withPositions = <T extends { Position: number }>(items: T[]): T[] =>
  items.map((item, index) => ({ ...item, Position: index }));

export const moveItem = <T>(items: T[], index: number, direction: -1 | 1): T[] => {
  const target = index + direction;
  if (target < 0 || target >= items.length) return items;
  const next = [...items];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

/** Duplicated questions/sections get fresh identities; jump targets inside a duplicated form are remapped. */
export const duplicateQuestion = (question: Question): Question => ({ ...question, ItemId: uuidv4() });

export const clearQuestionAnswer = (question: Question): Question => {
  switch (question.FormatType) {
    case 'short-text':
    case 'long-text':
    case 'date':
      return { ...question, Answer: '' };
    case 'multiple-choice':
      return { ...question, McOptionsValue: '' };
    case 'checkboxes':
      return { ...question, CbOptionsValues: [] };
    case 'rating':
    case 'nps':
      return { ...question, Rating: 0 };
    case 'likert-scale':
      return { ...question, LikertStatements: (question.LikertStatements ?? []).map((s) => ({ ...s, Value: [] })) };
    default:
      return question;
  }
};

/** Normalises stored questions (older records had plain-string options / scalar likert values). */
export const normalizeLoadedQuestion = (raw: Partial<Question> & Record<string, unknown>): Question => {
  const options = ((raw.Options as unknown[]) ?? []).map((option) =>
    typeof option === 'string'
      ? { Label: option, Value: option, Action: 'continue' as const }
      : { ...(option as QuestionOption), Action: (option as QuestionOption).Action ?? 'continue' }
  );

  const likertStatements = ((raw.LikertStatements as Array<{ Statement: string; Value?: string | string[] }>) ?? []).map(
    (statement) => ({
      Statement: statement.Statement,
      Value: Array.isArray(statement.Value) ? statement.Value : statement.Value ? [statement.Value] : [],
    })
  );

  return {
    ...createQuestion((raw.FormatType as QuestionFormatType) ?? 'short-text'),
    ...(raw as Partial<Question>),
    Options: options,
    LikertLabels: (raw.LikertLabels as string[]) ?? [],
    LikertStatements: likertStatements,
  };
};
